import type { PublicPurchaseProjection } from '~/modules/public/purchase/domain/public-purchase.types'

type Translate = (key: string, values?: Record<string, unknown>) => string
type FormatMoney = (amount: number, currency: string) => string

/**
 * Price shown under each locality chip before anything is reserved. A box sold chair by chair is priced
 * per chair while a whole box or table is priced per unit, so each kind gets its own figure instead of one
 * mixed minimum.
 */
export function publicLocalityPriceCaptions(projection: PublicPurchaseProjection, t: Translate, formatMoney: FormatMoney) {
  const seatName = t('public.purchase.priceUnits.seat')
  return Object.fromEntries(projection.localities.map((locality) => {
    const { currency } = locality.price
    if (locality.availableQuantity <= 0) return [locality.id, t('public.purchase.soldOut')]
    if (locality.saleMode === 'general-admission') return [locality.id, formatMoney(locality.price.amount, currency)]
    const amountsByUnit = new Map<string, number[]>()
    for (const unit of projection.units) {
      if (unit.localityId !== locality.id || !unit.available) continue
      const perChair = ((unit.saleTypeCode ?? locality.saleTypeCode) === 'INDIVIDUAL' && unit.isAtomic === false)
        || (unit.capacity ?? 1) <= 1
      // Whole units keep the tenant's own name for them (e.g. PALCO), as the cart does.
      const name = perChair
        ? seatName
        : unit.unitLabel?.trim() || t(`public.purchase.priceUnits.${unit.kind === 'group' ? unit.groupKind : 'box'}`)
      amountsByUnit.set(name, [...(amountsByUnit.get(name) ?? []), unit.price.amount])
    }
    if (!amountsByUnit.size) return [locality.id, t('public.purchase.soldOut')]
    return [locality.id, [...amountsByUnit]
      .sort(([name]) => name === seatName ? -1 : 1)
      .map(([name, amounts]) => {
        const minimum = Math.min(...amounts)
        return t(minimum === Math.max(...amounts) ? 'public.purchase.unitPrice' : 'public.purchase.unitPriceFrom', {
          unit: name,
          price: formatMoney(minimum, currency)
        })
      })
      .join(' · ')]
  }))
}
